import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Descriptions,
  Tag,
  Button,
  Spin,
  Card,
  Row,
  Col,
  Statistic,
  Space,
  Alert,
  Empty,
  Table,
  Select,
  Typography,
  theme,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import PageHelp from '../components/PageHelp';
import { ExportButton } from '../components/ExportButton';
import {
  getTerminal,
  getTerminalTelemetry,
  getTerminalAlerts,
  getTerminalUsage,
  type Terminal,
  type ServiceLine,
  type LiveTelemetry,
  type DeviceAlertEntry,
  type TerminalUsage,
  type DailyDataUsage,
} from '../services/clarusService';
import TelemetryHistoryChart from '../components/TelemetryHistoryChart';
import AutoRefreshControl from '../components/AutoRefreshControl';
import UsageChart, { fmtDay } from '../components/UsageChart';
import {
  dailyUsageOf,
  previousBillingPeriod,
  hasAllowance,
  isOverageBilled,
} from '../utils/usageWindow';
import { generatePeriodOptions, type Period } from '../utils/dateRange';
import { formatGigabytes, formatGbNice, chooseGbUnit, formatGbIn } from '../utils/formatUsage';
import { priorityScope } from '../utils/subscriptionLabel';
import { useDisplaySettings } from '../theme/DisplaySettingsContext';
import { blurStyle } from '../utils/redact';

const { Option } = Select;

const fmtDate = (v: string | null | undefined): string => (v ? new Date(v).toLocaleString() : '—');

// Clarus billing periods end at exclusive midnight-UTC of the next day; the inclusive last day is the day before.
const inclusiveEnd = (endIso: string): string =>
  new Date(new Date(endIso).getTime() - 86_400_000).toISOString();

/** A Statistic whose GB value is scaled to its own largest sensible unit (GB → TB → PB). */
const GbStat: React.FC<{
  title: React.ReactNode;
  gb: number | null | undefined;
  valueStyle?: React.CSSProperties;
  extra?: React.ReactNode;
}> = ({ title, gb, valueStyle, extra }) => {
  const { value, unit } = formatGigabytes(gb);
  return (
    <Statistic
      title={title}
      value={value}
      valueStyle={valueStyle}
      suffix={
        extra ? (
          <>
            {unit} {extra}
          </>
        ) : (
          unit
        )
      }
    />
  );
};

/** Sum of the day's billed and non-billable usage. */
const dayTotal = (d: DailyDataUsage): number =>
  (d.priorityGB ?? 0) + (d.standardGB ?? 0) + (d.nonBillableGB ?? 0);

/** The Monday→Sunday week containing today, as ISO date strings. */
const currentWeekBounds = (): { start: string; end: string } => {
  const now = new Date();
  const day = now.getDay();
  const diffToMon = now.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(now.getFullYear(), now.getMonth(), diffToMon);
  const sunday = new Date(monday.getTime() + 6 * 86_400_000);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return { start: fmt(monday), end: fmt(sunday) };
};

type StatusKey = 'down' | 'over' | 'forecast' | 'ok';

/** Header state: offline, over allowance, forecast over, or healthy. */
const statusOf = (
  terminal: Terminal,
  usage: TerminalUsage | null
): { key: StatusKey; label: string; color: string } => {
  if (!terminal.isOnline) return { key: 'down', label: 'Down', color: 'red' };
  if (
    usage &&
    hasAllowance(usage) &&
    usage.remainingAllowance !== null &&
    usage.remainingAllowance <= 0
  ) {
    return { key: 'over', label: 'Over allowance', color: 'volcano' };
  }
  if (usage?.forecastToExceedAllowance)
    return { key: 'forecast', label: 'Heading over', color: 'orange' };
  return { key: 'ok', label: 'Online', color: 'green' };
};

/**
 * Starlink terminal detail: a usage panel (period summary, chart and daily
 * table), a performance panel (throughput and quality), and reference cards for
 * the terminal, its service line and its allocation. Usage is reported per
 * service line, so a line carrying more than one dish shows their combined
 * total.
 */
const TerminalDashboard: React.FC = () => {
  const { deviceId = '' } = useParams<{ deviceId: string }>();
  const { redactNames } = useDisplaySettings();
  const navigate = useNavigate();
  const { token } = theme.useToken();

  const [terminal, setTerminal] = useState<Terminal | null>(null);
  const [serviceLine, setServiceLine] = useState<ServiceLine | null>(null);
  const [telemetry, setTelemetry] = useState<LiveTelemetry | null>(null);
  const [alerts, setAlerts] = useState<DeviceAlertEntry[]>([]);

  // Current cycle drives the header state and This Month; Last Month is fetched on demand.
  const [currentUsage, setCurrentUsage] = useState<TerminalUsage | null>(null);
  const [lastMonthUsage, setLastMonthUsage] = useState<TerminalUsage | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [liveError, setLiveError] = useState<string | null>(null);
  const [usageError, setUsageError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);
  const [autoRefreshMs, setAutoRefreshMs] = useState(0);
  const [usageLoading, setUsageLoading] = useState(true);
  const [lastMonthLoading, setLastMonthLoading] = useState(false);

  const [period, setPeriod] = useState<Period>('thisMonth');

  const load = useCallback(
    async (signal?: AbortSignal) => {
      setError(null);
      setLiveError(null);
      setUsageError(null);
      setUsageLoading(true);
      try {
        // The stored record returns fast; the live sections proxy to Clarus and
        // fill in behind their own indicators.
        const detail = await getTerminal(deviceId, signal);
        if (signal?.aborted) return;
        setTerminal(detail.terminal);
        setServiceLine(detail.serviceLine);
        setLoading(false);

        const serviceLineNumber = detail.terminal.serviceLineNumber;

        const live = (async () => {
          try {
            const [t, a] = await Promise.all([
              getTerminalTelemetry(deviceId, signal),
              getTerminalAlerts(deviceId, signal),
            ]);
            if (signal?.aborted) return;
            setTelemetry(t.telemetry);
            setAlerts(a.devices || []);
          } catch (err: any) {
            if (!signal?.aborted) setLiveError(err?.message || 'Live telemetry is unavailable');
          }
        })();

        const usageLoad = (async () => {
          if (!serviceLineNumber) {
            if (!signal?.aborted) setUsageLoading(false);
            return;
          }
          try {
            const u = await getTerminalUsage(serviceLineNumber, undefined, signal);
            if (!signal?.aborted) setCurrentUsage(u.usage);
          } catch (err: any) {
            if (!signal?.aborted) setUsageError(err?.message || 'Usage data is unavailable');
          } finally {
            if (!signal?.aborted) setUsageLoading(false);
          }
        })();

        await Promise.all([live, usageLoad]);
      } catch (err: any) {
        if (!signal?.aborted) {
          setError(err?.message || 'Failed to load terminal');
          setUsageLoading(false);
        }
      } finally {
        if (!signal?.aborted) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [deviceId]
  );

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  // Last Month is a separate cycle, fetched the first time it is selected and cached.
  useEffect(() => {
    if (period !== 'lastMonth') return;
    const sln = terminal?.serviceLineNumber;
    const prev = previousBillingPeriod(currentUsage?.billingPeriod);
    if (!sln || !prev || lastMonthUsage || lastMonthLoading) return;
    const controller = new AbortController();
    setLastMonthLoading(true);
    (async () => {
      try {
        const u = await getTerminalUsage(sln, prev, controller.signal);
        if (!controller.signal.aborted) setLastMonthUsage(u.usage);
      } catch {
        // A missing previous period leaves the view empty rather than erroring the page.
      } finally {
        if (!controller.signal.aborted) setLastMonthLoading(false);
      }
    })();
    return () => controller.abort();
  }, [period, terminal, currentUsage, lastMonthUsage, lastMonthLoading]);

  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    setRefreshToken(t => t + 1);
    setLastMonthUsage(null);
    load();
  }, [load]);

  // The usage source for the selected period, its daily rows, and whether it is a
  // closed period (which drops every forward-looking figure).
  const view = useMemo(() => {
    const isClosed = period === 'lastMonth';
    const source = period === 'lastMonth' ? lastMonthUsage : currentUsage;
    let days = dailyUsageOf(source);
    let label: string;

    if (period === 'thisWeek') {
      const { start, end } = currentWeekBounds();
      days = days.filter(d => {
        const k = new Date(d.date).toISOString().slice(0, 10);
        return k >= start && k <= end;
      });
      label = 'This Week';
    } else if (period === 'lastMonth') {
      label = 'Last Month';
    } else {
      label = 'This Month';
    }
    return { source, days, label, isClosed, isWeek: period === 'thisWeek' };
  }, [period, currentUsage, lastMonthUsage]);

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !terminal) {
    return (
      <div className="page-wrapper" style={{ padding: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/starlink')}>
          All terminals
        </Button>
        <Alert
          style={{ marginTop: 16 }}
          type="error"
          showIcon
          message="Could not load this terminal"
          description={error || 'Terminal not found.'}
          action={
            <Button size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  const live = telemetry;
  const activeAlerts = alerts.flatMap(d => d.alerts || []);
  const softwareUpdatePending = alerts.some(d => d.softwareUpdatePending);
  const status = statusOf(terminal, currentUsage);

  // Header leads with the terminal's friendly name, then the vessel it's assigned
  // to, then the Starlink site; the client name sits in the sub-line.
  const clientName = terminal.clientName || serviceLine?.clientName || null;
  const headerName =
    terminal.allocation?.friendlyName ||
    terminal.allocation?.assetName ||
    serviceLine?.siteName ||
    terminal.siteName ||
    clientName ||
    terminal.deviceId;
  // The friendly name now leads the title, so the suffix is the service-line
  // nickname only — never the friendly name again, the SL number, or the header.
  const rawNickname = serviceLine?.nickname || null;
  const lineNickname =
    rawNickname && rawNickname !== terminal.serviceLineNumber && rawNickname !== headerName
      ? rawNickname
      : null;
  const headerTitle = lineNickname ? `${headerName} · ${lineNickname}` : headerName;

  const scope = priorityScope(serviceLine?.subscription.description);
  const subParts = [
    clientName && clientName !== headerName ? clientName : null,
    scope,
    terminal.kitSerialNumber ? `Kit ${terminal.kitSerialNumber}` : null,
    terminal.serviceLineNumber,
  ].filter(Boolean) as string[];

  const src = view.source;
  // The period shown in the panel header: the Mon–Sun week, or the billing cycle.
  const periodRange = view.isWeek
    ? (() => {
        const w = currentWeekBounds();
        return `${fmtDay(w.start)} – ${fmtDay(w.end)}`;
      })()
    : src
      ? `${fmtDay(src.startDate)} – ${fmtDay(inclusiveEnd(src.endDate))}`
      : '';
  const busiest = view.days.reduce<{ date: string; gb: number } | null>((best, d) => {
    const t = dayTotal(d);
    return !best || t > best.gb ? { date: d.date, gb: t } : best;
  }, null);
  const weekUsed = view.days.reduce((s, d) => s + dayTotal(d), 0);
  // One unit for the whole table, from its largest daily total, so the column stays comparable.
  const tableUnit = chooseGbUnit(view.days.reduce((m, d) => Math.max(m, dayTotal(d)), 0));

  const pageFilename = () =>
    `simscope-terminal-${(redactNames ? terminal.deviceId : headerName || terminal.deviceId)
      .replace(/[^\w-]+/g, '-')
      .toLowerCase()}-${new Date().toISOString().slice(0, 10)}`;

  return (
    <div className="page-wrapper" style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 4 }} gutter={[8, 8]}>
        <Col flex="auto">
          <Space wrap size={8}>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/starlink')}
              className="no-print"
            >
              All terminals
            </Button>
            <h2 style={{ margin: 0, ...blurStyle(redactNames) }}>{headerTitle}</h2>
            <Tag color={status.color}>{status.label}</Tag>
            {!terminal.isActive && <Tag color="red">Deactivated</Tag>}
          </Space>
        </Col>
        <Col>
          <Space className="no-print">
            <AutoRefreshControl
              onRefresh={handleRefresh}
              loading={refreshing}
              intervalMs={autoRefreshMs}
              onIntervalChange={setAutoRefreshMs}
            />
            <ExportButton filename={pageFilename} />
            <PageHelp
              title="Starlink Terminal Help"
              sections={[
                {
                  title: 'Where this data comes from',
                  content:
                    'Identity, serials, site and online state come from our database, refreshed hourly from Clarus.\n\nData usage and live telemetry are fetched from Clarus when you open this page, so they are current and can be briefly unavailable if Clarus is down. The stored record stays visible either way.',
                },
                {
                  title: 'Data usage',
                  content:
                    'Usage is reported per service line, not per terminal. Where a line carries more than one dish, the figures are their combined total. The split is Priority, Standard and Non-billable, exactly as Clarus reports it.',
                },
                {
                  title: 'Read-only',
                  content:
                    'The Clarus API is read-only, so terminals cannot be suspended, resumed or terminated here. Allocation is managed from the inventory.',
                },
              ]}
            />
          </Space>
        </Col>
      </Row>

      {subParts.length > 0 && (
        <Typography.Text
          type="secondary"
          style={{ display: 'block', marginBottom: 16, ...blurStyle(redactNames) }}
        >
          {subParts.join('  ·  ')}
        </Typography.Text>
      )}

      {activeAlerts.length > 0 && (
        <Alert
          style={{ marginBottom: 16 }}
          type="warning"
          showIcon
          message={`${activeAlerts.length} active alert${activeAlerts.length > 1 ? 's' : ''}`}
          description={
            <Space wrap>
              {activeAlerts.map(a => (
                <Tag color="orange" key={`${a.code}-${a.label}`}>
                  {a.label} ({a.code})
                </Tag>
              ))}
              {softwareUpdatePending && <Tag color="blue">Software update pending</Tag>}
            </Space>
          }
        />
      )}

      <Card
        style={{ marginBottom: 16 }}
        title={`Data usage${periodRange ? ` · ${periodRange}` : ''}`}
        extra={
          <Select<Period>
            size="small"
            value={period}
            onChange={setPeriod}
            style={{ width: 160 }}
            className="no-print"
          >
            {generatePeriodOptions()
              .filter(
                o => o.value === 'thisWeek' || o.value === 'thisMonth' || o.value === 'lastMonth'
              )
              .map(({ label, value }) => (
                <Option key={value} value={value}>
                  {label}
                </Option>
              ))}
          </Select>
        }
      >
        {usageError ? (
          <Alert
            type="warning"
            showIcon
            message="Usage data unavailable"
            description={usageError}
          />
        ) : usageLoading || (period === 'lastMonth' && lastMonthLoading) ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : !src ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              terminal.serviceLineNumber
                ? 'No usage data for this period'
                : 'No service line to report usage for'
            }
          />
        ) : (
          <>
            {view.isWeek ? (
              <Row gutter={[24, 12]} style={{ marginBottom: 16 }}>
                <Col xs={12} md={6}>
                  <GbStat title="Used this week" gb={weekUsed} />
                </Col>
                <Col xs={12} md={6}>
                  <GbStat
                    title="Avg / day"
                    gb={view.days.length ? weekUsed / view.days.length : 0}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <GbStat
                    title="Busiest day"
                    gb={busiest ? busiest.gb : null}
                    extra={
                      busiest ? (
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {fmtDay(busiest.date)}
                        </Typography.Text>
                      ) : undefined
                    }
                  />
                </Col>
                <Col xs={12} md={6}>
                  <GbStat
                    title="Billing period to date"
                    gb={currentUsage?.totalUsage}
                    extra={
                      hasAllowance(currentUsage) ? (
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          of {formatGbNice(currentUsage?.subscriptionAllowance)}
                        </Typography.Text>
                      ) : undefined
                    }
                  />
                </Col>
              </Row>
            ) : (
              <Row gutter={[24, 12]} style={{ marginBottom: 16 }}>
                <Col xs={12} md={6}>
                  <GbStat title="Used" gb={src.totalUsage} />
                </Col>
                {!view.isClosed && (
                  <Col xs={12} md={6}>
                    <GbStat
                      title={`Forecast by ${fmtDay(inclusiveEnd(src.endDate))}`}
                      gb={src.forecastUsage}
                      valueStyle={
                        src.forecastToExceedAllowance ? { color: token.colorError } : undefined
                      }
                    />
                  </Col>
                )}
                <Col xs={12} md={6}>
                  {hasAllowance(src) ? (
                    <GbStat title="Allowance" gb={src.subscriptionAllowance} />
                  ) : (
                    <Statistic
                      title="Allowance"
                      value="No allowance"
                      valueStyle={{ fontSize: 20 }}
                    />
                  )}
                </Col>
                {view.isClosed &&
                  hasAllowance(src) &&
                  src.totalUsage !== null &&
                  src.subscriptionAllowance !== null && (
                    <Col xs={12} md={6}>
                      <GbStat
                        title="Overage"
                        gb={Math.max(0, src.totalUsage - src.subscriptionAllowance)}
                        valueStyle={
                          src.totalUsage > src.subscriptionAllowance
                            ? { color: token.colorError }
                            : undefined
                        }
                      />
                    </Col>
                  )}
              </Row>
            )}

            {!hasAllowance(src) && (
              <Typography.Text
                type="secondary"
                style={{ fontSize: 12, display: 'block', marginBottom: 12 }}
              >
                {isOverageBilled(src)
                  ? 'This plan has no included allowance — usage is billed as overage.'
                  : 'Clarus reports no allowance for this service line, so there is none to measure against.'}
              </Typography.Text>
            )}

            <UsageChart
              days={view.days}
              overlay={
                view.isWeek
                  ? undefined
                  : {
                      cumulative: true,
                      allowance: hasAllowance(src) ? src.subscriptionAllowance : null,
                      forecastTotal: view.isClosed ? null : src.forecastUsage,
                      periodEnd: view.isClosed ? null : inclusiveEnd(src.endDate),
                    }
              }
            />

            {view.days.length > 0 && (
              <Table<DailyDataUsage>
                style={{ marginTop: 16 }}
                size="small"
                rowKey="date"
                dataSource={[...view.days].sort((a, b) => b.date.localeCompare(a.date))}
                pagination={false}
                scroll={{ x: 'max-content' }}
                summary={() => {
                  const t = view.days.reduce(
                    (acc, d) => ({
                      p: acc.p + (d.priorityGB ?? 0),
                      s: acc.s + (d.standardGB ?? 0),
                      n: acc.n + (d.nonBillableGB ?? 0),
                    }),
                    { p: 0, s: 0, n: 0 }
                  );
                  return (
                    <Table.Summary.Row style={{ background: token.colorPrimaryBg }}>
                      <Table.Summary.Cell index={0}>
                        <strong>Period total</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1}>{formatGbNice(t.p)}</Table.Summary.Cell>
                      <Table.Summary.Cell index={2}>{formatGbNice(t.s)}</Table.Summary.Cell>
                      <Table.Summary.Cell index={3}>{formatGbNice(t.n)}</Table.Summary.Cell>
                      <Table.Summary.Cell index={4}>
                        <strong>{formatGbNice(t.p + t.s + t.n)}</strong>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  );
                }}
                columns={[
                  {
                    title: 'Date',
                    dataIndex: 'date',
                    key: 'date',
                    render: (d: string) => fmtDay(d),
                  },
                  {
                    title: 'Priority',
                    dataIndex: 'priorityGB',
                    key: 'priorityGB',
                    render: (v: number) => formatGbIn(v, tableUnit),
                  },
                  {
                    title: 'Standard',
                    dataIndex: 'standardGB',
                    key: 'standardGB',
                    render: (v: number) => formatGbIn(v, tableUnit),
                  },
                  {
                    title: 'Non-billable',
                    dataIndex: 'nonBillableGB',
                    key: 'nonBillableGB',
                    render: (v: number) => formatGbIn(v, tableUnit),
                  },
                  {
                    title: 'Total',
                    key: 'total',
                    render: (_: unknown, d: DailyDataUsage) => (
                      <strong>{formatGbIn(dayTotal(d), tableUnit)}</strong>
                    ),
                  },
                ]}
              />
            )}
          </>
        )}
      </Card>

      <Card
        style={{ marginBottom: 16 }}
        title="Performance as reported by the terminal"
        size="small"
      >
        {liveError && (
          <Alert
            style={{ marginBottom: 12 }}
            type="warning"
            showIcon
            message="Live telemetry unavailable"
            description={`${liveError} The stored record is still shown below.`}
          />
        )}
        <TelemetryHistoryChart
          deviceId={terminal.deviceId}
          refreshToken={refreshToken}
          layout="split"
        />
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Terminal" size="small">
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Device ID">
                <span style={blurStyle(redactNames)}>{terminal.deviceId}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Kit serial">
                <span style={blurStyle(redactNames)}>{terminal.kitSerialNumber || '—'}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Dish serial">
                <span style={blurStyle(redactNames)}>{terminal.dishSerialNumber || '—'}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Software">
                {live?.runningSoftwareVersion || terminal.runningSoftwareVersion || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Last reading">
                {fmtDate(live?.timestamp || terminal.lastTelemetryAt)}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Service line" size="small">
            {serviceLine ? (
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label="Number">
                  <span style={blurStyle(redactNames)}>{serviceLine.serviceLineNumber}</span>
                </Descriptions.Item>
                <Descriptions.Item label="Plan">
                  {[scope, serviceLine.subscription.description].filter(Boolean).join(' · ') || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  {serviceLine.isActive === null ? (
                    '—'
                  ) : (
                    <Tag color={serviceLine.isActive ? 'green' : 'red'}>
                      {serviceLine.isActive ? 'Active' : 'Inactive'}
                    </Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Billing period">
                  {currentUsage
                    ? `${fmtDay(currentUsage.startDate)} → ${fmtDay(inclusiveEnd(currentUsage.endDate))}`
                    : '—'}
                  {currentUsage && currentUsage.remainingDays > 0 && (
                    <Typography.Text type="secondary">
                      {' '}
                      · {currentUsage.remainingDays} day
                      {currentUsage.remainingDays === 1 ? '' : 's'} remaining
                    </Typography.Text>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Allowance">
                  {hasAllowance(currentUsage)
                    ? `${formatGbNice(currentUsage?.subscriptionAllowance)} per period`
                    : 'No allowance'}
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No service line data synced yet"
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default TerminalDashboard;
